import type { ColorSoundClientConfig, SubjectTypeEnumType, TriggerEnumType } from '@mark43/cad-api';
import { action, computed, makeObservable, observable } from 'mobx';
import {
    buildColorSoundConfigIndex,
    ConfigLookup,
    ColorSoundConfigIndex,
    PaletteTokens,
    resolveActionSound,
    resolveVisualColor,
} from './color-sound-config-resolver';
import { ALL_AGENCY_TYPES } from './color-sound-config-types';
import { logErrorMessage } from '../monitoring/log';
import type { SoundName } from '../sounds/play-sound';

/**
 * - `OFF`: `cad.configurable_colors_sounds.enabled` is off, so bootstrap sent no config.
 * - `ON`: the department's config applies.
 * - `UNREADABLE`: the flag is on but this client couldn't read the config. It behaves like `OFF`,
 *   today's colors and sounds, rather than failing the whole bootstrap.
 */
type ColorSoundConfigState =
    | { status: 'OFF' }
    | { status: 'ON'; index: ColorSoundConfigIndex }
    | { status: 'UNREADABLE' };

export interface ConfiguredSoundSubject {
    subjectType: SubjectTypeEnumType;
    subjectId: number | null | undefined;
    agencyTypeGlobalAttrId?: number | null;
}

const NO_SUBJECT: ConfiguredSoundSubject = { subjectType: 'NONE', subjectId: 0 };

const NOT_MATCHED = { matched: false } as const;

/**
 * Holds the department's color/sound configuration from bootstrap, and is the only thing that knows
 * whether it applies. Module-level (not context) because non-React code such as the audible alerts
 * resolves against it too. Reading a query inside a MobX reaction or observer tracks config changes.
 */
export class ColorSoundConfigStore {
    private state: ColorSoundConfigState = { status: 'OFF' };

    constructor() {
        makeObservable<ColorSoundConfigStore, 'state'>(this, {
            state: observable.ref,
            isEnabled: computed,
            hasPriorityColors: computed,
            setConfig: action,
        });
    }

    /** The department's configuration applies: the flag is on and this client could read it. */
    get isEnabled(): boolean {
        return this.state.status === 'ON';
    }

    /** The department has configured at least one event priority color. */
    get hasPriorityColors(): boolean {
        return this.state.status === 'ON' && this.state.index.hasPriorityColors;
    }

    setConfig(config: ColorSoundClientConfig | null | undefined): void {
        if (!config) {
            this.state = { status: 'OFF' };
            return;
        }
        try {
            this.state = { status: 'ON', index: buildColorSoundConfigIndex(config) };
        } catch (error) {
            this.state = { status: 'UNREADABLE' };
            logErrorMessage(
                'Could not read the color and sound config; using the default colors and sounds'
            );
        }
    }

    /**
     * The sound to play for a trigger, or null for none. While the configuration applies, the
     * department's row decides, "No sound" included, and with no row the default sound plays. Otherwise
     * the trigger's legacy Audio Alerts preference gates the default sound, as it did before
     * configurable sounds; pass `true` for a trigger that has no preference.
     */
    resolveSound({
        triggerType,
        subject = NO_SUBJECT,
        defaultSound,
        isLegacyPreferenceOn,
    }: {
        triggerType: TriggerEnumType;
        subject?: ConfiguredSoundSubject;
        defaultSound: SoundName;
        isLegacyPreferenceOn: boolean;
    }): SoundName | null {
        if (this.state.status !== 'ON') {
            return isLegacyPreferenceOn ? defaultSound : null;
        }
        const configured = resolveActionSound(
            this.state.index,
            triggerType,
            subject.subjectType,
            subject.subjectId,
            subject.agencyTypeGlobalAttrId
        );
        return configured.matched ? configured.value : defaultSound;
    }

    /** Each distinct configured palette color, for renderers that tint their own images. */
    getConfiguredColors(): PaletteTokens[] {
        if (this.state.status !== 'ON') {
            return [];
        }
        const { visualColors, colorLookups } = this.state.index;
        const colors = new Set<PaletteTokens>();
        visualColors.forEach(function addConfiguredColor(colorName) {
            const lookup = colorName ? colorLookups.get(colorName) : undefined;
            if (lookup?.matched && lookup.value) {
                colors.add(lookup.value);
            }
        });
        return Array.from(colors);
    }

    /** `matched: false` means today's default color applies. */
    resolvePriorityColor(
        priorityId: number | null | undefined,
        agencyTypeGlobalAttrId: number | null | undefined
    ): ConfigLookup<PaletteTokens> {
        if (this.state.status !== 'ON') {
            return NOT_MATCHED;
        }
        return resolveVisualColor(
            this.state.index,
            'EVENT_PRIORITY',
            priorityId,
            agencyTypeGlobalAttrId
        );
    }

    /** `matched: false` means today's default color applies. */
    resolveUnitStateColor(unitStateId: number | null | undefined): ConfigLookup<PaletteTokens> {
        if (this.state.status !== 'ON') {
            return NOT_MATCHED;
        }
        return resolveVisualColor(this.state.index, 'UNIT_STATE', unitStateId, ALL_AGENCY_TYPES);
    }
}

export const colorSoundConfigStore = new ColorSoundConfigStore();
